// Libraries
import React, {useEffect, useState} from 'react';
import {Row, Modal, Descriptions, Space, Popconfirm} from 'antd';
import {useAppDispatch} from '../../store/store';
import {useSelector} from "react-redux";
import {ReloadOutlined, StopOutlined, TagsOutlined} from "@ant-design/icons";
import modal from 'antd/lib/modal';

// Store
import { getDetailByOrderNumber } from '../../store/redux/actions/orderActions';
import * as orderSelectors from "../../store/redux/selectors/orderSelectors";
import {cancelGuide} from "../../store/redux/actions/shipmentActions";

// Components
import ButtonComponent from '../subComponents/ButtonComponent';
import TableComponent from "../subComponents/TableComponent";
import ItemsDrawer from "./ItemsDrawer";
import ReCreateGuideModal from "./ReCreateGuideModal";

// Utils
import {tableColumns} from "./tableColumns";

// Types
import {PaginationType} from "../../types/sharedTypes";
import {PackageItemType, ShipmentType} from "../../types/shipmentTypes";


type PropsType = {
  orderNumber: string;
  onToggleModal: (visible: boolean, orderNumber: string) => void;
};
const OrdersFormModal = (props: PropsType) => {
  const dispatch = useAppDispatch();
  const { orderNumber, onToggleModal } = props;
  const detail = useSelector(orderSelectors.orderDetailSelector);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showReCreateModal, setShowReCreateModal] = useState(false);
  const [packageItems, setPackageItems] = useState<PackageItemType[]>([]);
  const [shipmentToReCreate, setShipmentToReCreate] = useState<ShipmentType>();
  const [paginationData, setPaginationData] = useState<PaginationType>(
    { page: 1, size: 10 }
  );

  useEffect(() => {
    dispatch(getDetailByOrderNumber(orderNumber));
  }, []);

  const handleDrawer = (visible: boolean, packageItems: PackageItemType[]) => {
    setShowDrawer(visible);
    setPackageItems(packageItems);
  }

  const handleReCreateGuideModal = (visible: boolean, shipment?: ShipmentType) => {
    setShowReCreateModal(visible);
    setShipmentToReCreate(shipment);
  }

  const handleCancelGuide = (
    externalGuideId: string,
    allianceId: string
  ) => {
    const sameGuideCounter = detail.shipments!.filter(shipment => shipment.externalGuideId === externalGuideId).length;
    if (sameGuideCounter > 1) {
      modal.confirm({
        content: 'En esta orden hay otros despachos con la misma guía que también serán cancelados. ¿Deseas continuar?',
        onOk: () => {
          dispatch(cancelGuide({ externalGuideId, allianceId }, detail.orderNumber));
        },
        okText: 'Continuar',
        cancelText: 'Cancelar',
        keyboard: false,
        closable: false,
      })
    } else {
      dispatch(cancelGuide({ externalGuideId, allianceId }, detail.orderNumber));
    }
  }

  const actionButtons = (shipment: ShipmentType) => {
    return (
      <Space>
        {shipment.packageItems.length > 0
          && <TagsOutlined className={'cursor-pointer hover:text-blue-dkt-400'}
                           title={'Ver artículos del paquete'}
                           onClick={() => handleDrawer(true, shipment.packageItems)} />
        }
        {shipment.internalTracking === 'created' &&
          <Popconfirm title={'¿Confirmas que deseas cancelar este despacho?'}
                      okText={'Confirmar'}
                      cancelText={'Cancelar'}
                      onConfirm={() => handleCancelGuide(shipment.externalGuideId, shipment.allianceId)}>
            <StopOutlined className={'cursor-pointer hover:text-red-dkt-400'}
                          title={'Cancelar guía'} />
          </Popconfirm>
        }
        {shipment.internalTracking === 'canceled' &&
          <ReloadOutlined className={'cursor-pointer hover:text-blue-dkt-400'}
                          title={'Volver a crear guía'}
                          onClick={() => handleReCreateGuideModal(true, shipment)} />
        }
      </Space>
    )
  };

  return (
    <Modal title={'Detalle de la orden'}
           width={1000}
           className={showReCreateModal ? 'hidden' : ''}
           open={true} maskClosable={true} closable={true} keyboard={false} destroyOnClose={true}
           onCancel={() => onToggleModal(false, '')}
           footer={[
             <ButtonComponent key={'cancel'} text={'Cerrar'}
                              className={'w-32 h-8 bg-white border border-grey-dkt-300 leading-4 text-grey-dkt-700 ' +
                                'hover:bg-grey-dkt-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-dkt-400'}
                              onClick={() => onToggleModal(false, '')} />,
           ]}>
      <Row>
        <Descriptions className={'w-full [&>div>table>tbody>tr>th]:font-bold last:[&>div>table>tbody>tr]:hidden'}
                      layout={'vertical'}
                      bordered
                      size={'small'}
        >
          <Descriptions.Item label={"Número de orden"}>{detail.orderNumber}</Descriptions.Item>
          <Descriptions.Item label={"Contacto"}>{detail.contact}</Descriptions.Item>
          <Descriptions.Item label={"Documento del contacto"}>{detail.contactDocument}</Descriptions.Item>
          <Descriptions.Item label={"Código de ciudad"}>{detail.cityCode}</Descriptions.Item>
          <Descriptions.Item label={"Dirección"}>{detail.clientAddress}</Descriptions.Item>
          <Descriptions.Item label={"Detalles"}>{detail.details}</Descriptions.Item>
          <Descriptions.Item label={"Teléfono"}>{detail.phone}</Descriptions.Item>
          <Descriptions.Item label={"Correo"} span={2}>{detail.email}</Descriptions.Item>
          <Descriptions.Item label={"Envíos"}>{}</Descriptions.Item>
        </Descriptions>

        <TableComponent className={''}
                        pageSize={paginationData.size}
                        currentPage={paginationData.page}
                        total={detail.shipments!.length}
                        dataSource={detail.shipments!}
                        scrollX={1700}
                        rowKey={'shipmentId'}
                        columns={tableColumns(actionButtons)}
                        onChangePage={setPaginationData} />
      </Row>

      {showDrawer &&
        <ItemsDrawer packageItems={packageItems}
                     onToggleDrawer={handleDrawer} />
      }
      {showReCreateModal &&
        <ReCreateGuideModal shipmentToReCreate={shipmentToReCreate}
                            orderNumber={detail.orderNumber}
                            onToggleModal={handleReCreateGuideModal} />
      }
    </Modal>
  );
}

export default OrdersFormModal;